import { useCallback, useEffect, useState } from 'react';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { templateService } from '../services/templateService';
import { getSampleSheetUrl } from '../constants/templateApi';
import { parseBulkUploadError } from '../utils/templateSendUtils';
import { validateBulkExcelFile } from '../utils/bulkSheetValidation';
import { downloadBulkErrorSheet } from '../utils/buildBulkErrorSheet';

export default function useLeanBulkSend({ template } = {}) {
  const { showSnackBar } = useSnackBar();
  const [tempDataFile, setTempDataFile] = useState(null);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [isDiscardLoading, setIsDiscardLoading] = useState(false);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [errorRowResults, setErrorRowResults] = useState([]);
  const [errorSourceFile, setErrorSourceFile] = useState(null);
  const [progressBar, setProgressBar] = useState(0);
  const [job, setJob] = useState(null);
  const [draft, setDraft] = useState(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [isRetryLoading, setIsRetryLoading] = useState(false);

  const refreshJob = useCallback(async (jobId) => {
    const res = await templateService.getLeanBulkSend(jobId);
    if (res?.status === 200) {
      setJob((prev) => ({
        ...prev,
        ...(res?.data?.data ?? {}),
        counts: res?.data?.data?.counts ?? prev?.counts,
      }));
    }
  }, []);

  useEffect(() => {
    const jobId = job?.job_id;
    const pending = (job?.counts?.pending || 0) + (job?.counts?.processing || 0);
    if (!jobId || pending <= 0) return undefined;
    const timer = setInterval(() => {
      refreshJob(jobId);
    }, 3000);
    return () => clearInterval(timer);
  }, [job?.job_id, job?.counts?.pending, job?.counts?.processing, refreshJob]);

  const onBulkFileSelect = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setBulkUploadError('');
    setErrorRowResults([]);
    setErrorSourceFile(null);
    const result = await validateBulkExcelFile(file, template);
    if (!result.valid) {
      const rowResults = result.rowResults || [];
      const hasRowErrors = rowResults.some((entry) => entry.errors?.length);
      if (hasRowErrors) {
        setErrorRowResults(rowResults);
        setErrorSourceFile(file);
      } else {
        setBulkUploadError(result.message);
      }
      setTempDataFile(null);
      if (event?.target) event.target.value = '';
      return;
    }
    setTempDataFile(file);
  }, [template]);

  const onBulkFileRemove = useCallback(() => {
    setTempDataFile(null);
    setBulkUploadError('');
    setErrorRowResults([]);
    setErrorSourceFile(null);
    setProgressBar(0);
  }, []);

  useEffect(() => {
    setDraft(null);
    setJob(null);
    setTempDataFile(null);
    setErrorRowResults([]);
    setErrorSourceFile(null);
    setBulkUploadError('');
    setProgressBar(0);
    setScheduledAt('');
  }, [template?.bot_number, template?.wa_Id]);

  const uploadFile = useCallback(async (file) => {
    if (!file) return { success: false };
    setIsUploadLoading(true);
    setBulkUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await templateService.createLeanBulkSend(
        formData,
        {
          bot_number: template?.bot_number,
          wa_Id: template?.wa_Id,
        },
        (event) => {
          const total = event.total || 1;
          setProgressBar(Math.floor((event.loaded * 100) / total));
        }
      );
      if (res?.status === 200) {
        const data = res?.data?.data || {};
        setDraft({
          job_id: data.job_id,
          queued: data.queued ?? 0,
          duplicates: data.duplicates ?? 0,
          status: data.status || 'draft',
          total_count: data.total_count ?? (data.queued ?? 0),
          optout_count: data.optout_count ?? 0,
          eligible_count: data.eligible_count ?? (data.queued ?? 0),
          ...data,
        });
        setTempDataFile(null);
        setProgressBar(0);
        if (data.optout_count > 0) {
          showSnackBar(
            `Sheet checked. ${data.eligible_count ?? data.queued ?? 0} eligible, ${data.optout_count} opted out excluded.`,
            'info'
          );
        } else {
          showSnackBar('Sheet checked. Press Send to start.', 'success');
        }
        return { success: true };
      }
      const errorMessage = parseBulkUploadError(res?.response ?? res);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } catch (error) {
      const errorMessage = parseBulkUploadError(error?.response ?? error);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } finally {
      setIsUploadLoading(false);
    }
  }, [showSnackBar, template]);

  const uploadBulkFile = useCallback(async () => uploadFile(tempDataFile), [tempDataFile, uploadFile]);

  const uploadPreparedFile = useCallback(async (file) => {
    if (!file) return { success: false };
    setBulkUploadError('');
    setErrorRowResults([]);
    setErrorSourceFile(null);
    const result = await validateBulkExcelFile(file, template);
    if (!result.valid) {
      const rowResults = result.rowResults || [];
      const hasRowErrors = rowResults.some((entry) => entry.errors?.length);
      if (hasRowErrors) {
        setErrorRowResults(rowResults);
        setErrorSourceFile(file);
      } else {
        setBulkUploadError(result.message);
      }
      setTempDataFile(null);
      return { success: false };
    }
    return uploadFile(file);
  }, [template, uploadFile]);

  const sendDraft = useCallback(async () => {
    if (!draft?.job_id) return { success: false };

    let scheduledIso;
    if (scheduledAt) {
      const scheduledDate = new Date(scheduledAt);
      if (Number.isNaN(scheduledDate.getTime())) {
        showSnackBar('Invalid schedule time', 'error');
        return { success: false };
      }
      if (scheduledDate.getTime() <= Date.now()) {
        showSnackBar('Schedule time must be in the future', 'error');
        return { success: false };
      }
      scheduledIso = scheduledDate.toISOString();
    }

    setIsUploadLoading(true);
    setBulkUploadError('');
    try {
      const res = await templateService.startLeanBulkSend(draft.job_id, {
        scheduled_at: scheduledIso,
      });
      if (res?.status === 200) {
        const data = res?.data?.data || {};
        const queued = data.queued || draft.queued || 0;
        const jobStatus = data.status || 'queued';
        setJob({
          job_id: draft.job_id,
          status: jobStatus,
          total_count: draft.total_count ?? queued,
          optout_count: draft.optout_count ?? 0,
          eligible_count: draft.eligible_count ?? queued,
          counts: { pending: queued, processing: 0, sent: 0, failed: 0, draft: 0, total: queued },
          ...data,
        });
        setDraft(null);
        setProgressBar(0);
        setScheduledAt('');
        showSnackBar(
          jobStatus === 'scheduled' ? 'Bulk send scheduled' : 'Bulk send started',
          'success'
        );
        refreshJob(draft.job_id);
        return { success: true };
      }
      const errorMessage = parseBulkUploadError(res?.response ?? res);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } catch (error) {
      const errorMessage = parseBulkUploadError(error?.response ?? error);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } finally {
      setIsUploadLoading(false);
    }
  }, [draft, refreshJob, scheduledAt, showSnackBar]);

  const discardDraft = useCallback(async () => {
    if (!draft?.job_id) return { success: false };
    setIsDiscardLoading(true);
    setBulkUploadError('');
    try {
      const res = await templateService.discardLeanBulkSend(draft.job_id);
      if (res?.status === 200) {
        setDraft(null);
        setProgressBar(0);
        showSnackBar('Removed from queue', 'success');
        return { success: true };
      }
      const errorMessage = parseBulkUploadError(res?.response ?? res);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } catch (error) {
      const errorMessage = parseBulkUploadError(error?.response ?? error);
      setBulkUploadError(errorMessage);
      showSnackBar(errorMessage, 'error');
      return { success: false };
    } finally {
      setIsDiscardLoading(false);
    }
  }, [draft, showSnackBar]);

  const retryFailed = useCallback(async () => {
    if (!job?.job_id) return;
    setIsRetryLoading(true);
    try {
      await templateService.retryLeanBulkSend(job.job_id);
      await refreshJob(job.job_id);
    } finally {
      setIsRetryLoading(false);
    }
  }, [job?.job_id, refreshJob]);

  const downloadErrorFile = useCallback(async () => {
    if (!errorSourceFile || !errorRowResults.length) return;
    await downloadBulkErrorSheet({ file: errorSourceFile, rowResults: errorRowResults });
  }, [errorRowResults, errorSourceFile]);

  const downloadSampleFile = useCallback(() => {
    const url = getSampleSheetUrl(template?.wa_Id);
    if (url) window.open(url, '_blank');
  }, [template]);

  const errorRowCount = errorRowResults.filter((entry) => entry.errors?.length).length;
  const counts = job?.counts || {};
  const total = counts.total || 0;
  const done = (counts.sent || 0) + (counts.failed || 0);
  const sendProgress = total ? Math.floor((done * 100) / total) : 0;

  return {
    tempDataFile,
    setTempDataFile,
    isUploadLoading,
    isDiscardLoading,
    bulkUploadError,
    errorRowCount,
    progressBar,
    job,
    draft,
    counts,
    sendProgress,
    isRetryLoading,
    onBulkFileSelect,
    onBulkFileRemove,
    uploadBulkFile,
    uploadPreparedFile,
    sendDraft,
    discardDraft,
    retryFailed,
    downloadErrorFile,
    downloadSampleFile,
    scheduledAt,
    setScheduledAt,
  };
}
